import { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import type { IncidentRow } from '@/shared/types/incident.ts';

interface Props {
  incident: IncidentRow;
  onClose: () => void;
  onConfirm: (causaRaiz: string) => void;
  isSubmitting: boolean;
}

export function CloseIncidentModal({ incident, onClose, onConfirm, isSubmitting }: Props) {
  const [causaRaiz, setCausaRaiz] = useState('');
  const [error, setError] = useState('');

  const isCritica = incident.severidade === 'CRITICA';

  function handleSubmit() {
    if (!causaRaiz.trim()) {
      setError('Causa raiz é obrigatória');
      return;
    }
    setError('');
    onConfirm(causaRaiz.trim());
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold">Fechar Incidente</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          {/* Aviso CRITICA */}
          {isCritica && (
            <div className="flex items-start gap-2 rounded-md border px-3 py-2 text-sm" style={{ borderColor: 'var(--color-status-down)', color: 'var(--color-status-down)' }}>
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>Incidente crítico — causa raiz obrigatória para fechamento</span>
            </div>
          )}

          <p className="text-sm text-gray-600 dark:text-gray-400">
            Encerrando: <strong>{incident.titulo}</strong>
          </p>

          {/* Causa raiz */}
          <div>
            <label className="mb-1 block text-sm font-medium">Causa Raiz</label>
            <textarea
              value={causaRaiz}
              onChange={(e) => {
                setCausaRaiz(e.target.value);
                if (error) setError('');
              }}
              rows={4}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
              placeholder="Descreva a causa raiz do incidente..."
            />
            {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Fechando...' : 'Fechar Incidente'}
          </button>
        </div>
      </div>
    </div>
  );
}
